import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFlow } from '../state/flowStore';

type Permission = 'pending' | 'live' | 'denied' | 'unsupported';

export function CameraScreen() {
  const nav = useNavigate();
  const { setCapturedImage } = useFlow();
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [permissionState, setPermissionState] = useState<Permission>('pending');
  const [capturing, setCapturing] = useState(false);
  const [viewport, setViewport] = useState({ w: 0, h: 0 });

  // Track viewport for the SVG mask
  useEffect(() => {
    function update() {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Request camera once on mount
  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setPermissionState('unsupported');
        return;
      }
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 1600 },
          },
          audio: false,
        });
        if (cancelled) {
          s.getTracks().forEach(t => t.stop());
          return;
        }
        setStream(s);
        setPermissionState('live');
      } catch (err) {
        console.error('[camera] getUserMedia failed:', err);
        setPermissionState('denied');
      }
    }

    start();
    return () => {
      cancelled = true;
    };
  }, []);

  // Attach the stream to the <video> once both exist. Runs after render,
  // so videoRef is guaranteed populated (we always render the video element).
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !stream) return;
    v.srcObject = stream;
    const onLoaded = () => v.play().catch(err => console.warn('[camera] play() rejected:', err));
    v.addEventListener('loadedmetadata', onLoaded);
    // Some browsers fire metadata before listener attaches; try immediate too
    v.play().catch(() => {});
    return () => v.removeEventListener('loadedmetadata', onLoaded);
  }, [stream]);

  // Stop tracks on unmount
  useEffect(() => {
    return () => {
      stream?.getTracks().forEach(t => t.stop());
    };
  }, [stream]);

  async function captureFromVideo() {
    const v = videoRef.current;
    if (!v || v.videoWidth === 0) return;
    setCapturing(true);

    try {
      const target = 1600; // max dimension — matches iOS preprocessing
      const aspect = v.videoWidth / v.videoHeight;
      const [w, h] =
        aspect >= 1 ? [target, Math.round(target / aspect)] : [Math.round(target * aspect), target];

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      // <video> is mirrored visually (CSS scaleX(-1)) but the raw stream is
      // un-mirrored — capture as-is so Perfect Corp sees reality.
      ctx.drawImage(v, 0, 0, w, h);

      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.85),
      );
      setCapturedImage(blob);
      nav('/soft-paywall');
    } finally {
      setCapturing(false);
    }
  }

  async function captureFromFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturedImage(file);
    nav('/soft-paywall');
  }

  // Oval geometry — driven by viewport so the dim mask and the gold border
  // are guaranteed to align (single source of truth).
  const ovalW = Math.min(viewport.w * 0.82, 360);
  const ovalH = ovalW * 1.28;
  const cx = viewport.w / 2;
  const cy = viewport.h / 2;
  const rx = ovalW / 2;
  const ry = ovalH / 2;

  const denied = permissionState === 'denied' || permissionState === 'unsupported';

  return (
    <div className="relative h-svh w-full bg-black overflow-hidden">
      {/* Always rendered — the stream attaches via effect once available */}
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          transform: 'scaleX(-1)',
          visibility: permissionState === 'live' ? 'visible' : 'hidden',
        }}
      />

      {/* While we wait for permission, show a soft hint instead of solid black */}
      {permissionState === 'pending' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-8">
          <span className="h-5 w-5 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          <p className="text-body text-white/70">Allow camera access to scan.</p>
        </div>
      )}

      {/* Dark overlay + matching oval border — single SVG, always aligned */}
      {viewport.w > 0 && (
        <svg
          className="absolute inset-0 pointer-events-none"
          width={viewport.w}
          height={viewport.h}
          aria-hidden
        >
          <defs>
            <mask id="oval-cutout" maskUnits="userSpaceOnUse">
              <rect width={viewport.w} height={viewport.h} fill="white" />
              <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="black" />
            </mask>
          </defs>
          <rect
            width={viewport.w}
            height={viewport.h}
            fill="black"
            fillOpacity="0.55"
            mask="url(#oval-cutout)"
          />
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill="none"
            stroke="rgb(212, 168, 78)"
            strokeOpacity="0.75"
            strokeWidth="1.5"
          />
        </svg>
      )}

      <div className="absolute top-12 inset-x-0 flex flex-col items-center gap-2 px-6">
        <span className="text-caption text-white/75 px-3.5 py-1.5 rounded-full bg-black/45 backdrop-blur-sm pointer-events-none">
          Fill the oval · face straight on · natural light
        </span>
        <span className="text-[10px] text-white/55 text-center max-w-[280px] leading-snug">
          By taking a photo you consent to biometric analysis per our{' '}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </span>
      </div>

      <div
        className="absolute inset-x-0 flex flex-col items-center gap-4 px-6"
        style={{ bottom: 'max(2.5rem, env(safe-area-inset-bottom))' }}
      >
        {denied ? (
          <>
            <p className="text-body text-white/80 text-center max-w-xs">
              We couldn't access your camera. Use a photo from your library instead.
            </p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="lume-cta max-w-xs"
            >
              Choose a photo
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={capturing || permissionState !== 'live'}
            onClick={captureFromVideo}
            className="relative h-20 w-20 rounded-full disabled:opacity-40 transition active:scale-95"
            aria-label="Capture"
          >
            <span className="absolute inset-0 rounded-full bg-white/10" />
            <span className="absolute inset-0 rounded-full border-[1.5px] border-accent" />
            <span
              className="absolute inset-3 rounded-full"
              style={{
                background: 'linear-gradient(180deg, #DCBA60 0%, #C89840 100%)',
                boxShadow: '0 4px 10px rgba(212, 168, 78, 0.4)',
              }}
            />
          </button>
        )}

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="text-caption text-white/65 underline-offset-2 hover:underline"
        >
          Choose from library
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="user"
          hidden
          onChange={captureFromFile}
        />
      </div>
    </div>
  );
}
